import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminPermissions } from "@/hooks/useAdminPermissions";
import { useModuleSettings } from "@/hooks/useModuleSettings";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Search, CornerDownLeft, ArrowUp, ArrowDown } from "lucide-react";
import { ADMIN_SEARCH_INDEX, scoreEntry, type SearchEntry } from "@/config/adminSearchIndex";

export type { SearchEntry };

/* ─── Hook ─── */
export function useAdminCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return { open, setOpen };
}

/* ─── Component ─── */
interface AdminCommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function AdminCommandPalette({ open, onOpenChange }: AdminCommandPaletteProps) {
  const navigate = useNavigate();
  const { hasPermission } = useAdminPermissions();
  const { isModuleEnabled } = useModuleSettings();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Permission + module filtered entries
  const allowedEntries = useMemo(
    () =>
      ADMIN_SEARCH_INDEX.filter((e) => {
        if (e.permission && !hasPermission(e.permission)) return false;
        if (e.moduleKey && !isModuleEnabled(e.moduleKey)) return false;
        return true;
      }),
    [hasPermission, isModuleEnabled]
  );

  // Search filter + ranking
  const results = useMemo(() => {
    if (!query.trim()) return allowedEntries;
    const q = query.toLowerCase().trim();
    return allowedEntries
      .map((e) => ({ e, score: scoreEntry(e, q) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => a.score - b.score || a.e.priority - b.e.priority)
      .map((r) => r.e);
  }, [query, allowedEntries]);

  // Group results
  const grouped = useMemo(() => {
    const groups: Record<string, SearchEntry[]> = {};
    results.forEach((r) => {
      if (!groups[r.category]) groups[r.category] = [];
      groups[r.category].push(r);
    });
    // Sort groups by first item priority
    return Object.entries(groups).sort(
      ([, a], [, b]) => (a[0]?.priority ?? 999) - (b[0]?.priority ?? 999)
    );
  }, [results]);

  // Flat list for keyboard navigation
  const flatResults = useMemo(() => grouped.flatMap(([, items]) => items), [grouped]);

  // Reset selection on query change
  useEffect(() => { setSelectedIndex(0); }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Scroll selected into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (entry: SearchEntry) => {
      onOpenChange(false);
      navigate(entry.route);
    },
    [navigate, onOpenChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (flatResults[selectedIndex]) handleSelect(flatResults[selectedIndex]);
      }
    },
    [flatResults, selectedIndex, handleSelect]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden bg-card border-border/60 shadow-2xl [&>button]:hidden">
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border/50">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search modules, pages, settings…"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-border/50 bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[360px] overflow-y-auto py-1">
          {flatResults.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Search className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">No results found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          ) : (
            grouped.map(([category, items]) => (
              <div key={category}>
                <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {category}
                </p>
                {items.map((entry) => {
                  const globalIdx = flatResults.indexOf(entry);
                  const isSelected = globalIdx === selectedIndex;
                  return (
                    <button
                      key={entry.key}
                      data-index={globalIdx}
                      onClick={() => handleSelect(entry)}
                      onMouseEnter={() => setSelectedIndex(globalIdx)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        isSelected ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/40"
                      }`}
                    >
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isSelected ? "bg-primary/20 text-primary" : "bg-muted/50 text-muted-foreground"
                      }`}>
                        <entry.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? "text-foreground" : ""}`}>
                          {entry.title}
                        </p>
                        {entry.subtitle && (
                          <p className="text-xs text-muted-foreground truncate">{entry.subtitle}</p>
                        )}
                      </div>
                      {isSelected && (
                        <CornerDownLeft className="h-3.5 w-3.5 text-primary shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-border/50 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <ArrowUp className="h-3 w-3" /><ArrowDown className="h-3 w-3" /> Navigate
          </span>
          <span className="flex items-center gap-1">
            <CornerDownLeft className="h-3 w-3" /> Open
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded border border-border/50 bg-muted/50 px-1 font-mono">ESC</kbd> Close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
